/**
 * Announcement ticker.
 *
 * Replaces Horizon's crossfade slider for the announcement bar: instead of one
 * message at a time with arrows to reach the others, every message rides past on a
 * continuous loop.
 *
 * The loop is seamless because the track is built in two identical halves and the
 * animation translates it by -50% — at which point the second half sits precisely
 * where the first started. That only lines up if the space after the LAST item is the
 * same as the space between items, which is why the stylesheet spaces them with
 * margin-inline-end on every child rather than with `gap` (N items have N margins but
 * only N-1 gaps, and that missing gap is a visible stutter once a loop).
 *
 * The second condition is the one this used to get wrong. A half must also be at
 * least as WIDE AS THE BAR. Each half used to be exactly one run of the messages, so
 * on a wide window with a few short messages the track ran out before the loop came
 * round: the messages slid away to the left and the bar sat empty until it restarted.
 * A half is now however many runs it takes to cover the bar, so there is always
 * something in the window.
 */
class AnnouncementTicker extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** The messages as authored — never clones, so a rebuild can always get back to them. */
  #originals = [];
  /** Runs of the messages in EACH half. */
  #runs = 0;
  #pxPerSecond = 60;
  #initialised = false;

  connectedCallback() {
    // Re-entry would read the clones as originals and multiply them every time. The
    // theme editor moves this element around, so it does happen.
    if (this.#initialised) return;

    const track = this.querySelector('.announcement-bar__track');
    if (!track) return;

    const originals = Array.from(track.children);
    if (originals.length === 0) return;

    this.#track = /** @type {HTMLElement} */ (track);
    this.#originals = originals;
    this.#initialised = true;

    // The block template hides every message after the first from assistive tech,
    // which is right for a slider showing one at a time and wrong here: they are all
    // on screen. The clones are the ones that should be silent — they are the same
    // sentences again.
    for (const el of originals) el.setAttribute('aria-hidden', 'false');

    // A reader who has asked for reduced motion gets no animation, so a second copy
    // would just be the messages repeated twice in a row. Leave the single copy and
    // let the stylesheet make the bar scrollable by hand instead.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    // `speed` is the section's existing setting and keeps its existing sense: a bigger
    // number is slower. Deriving the duration from the measured width rather than using
    // it directly keeps the messages moving at the same pace whether there are two of
    // them or six.
    this.#pxPerSecond = 240 / (Number(this.dataset.speed) || 4);

    this.#layout();

    // Web fonts land after first paint and change how wide the sentences are; without
    // this both the pace and the number of runs are decided by the fallback font.
    if (document.fonts?.ready) document.fonts.ready.then(() => this.#layout());

    // Observes the BAR, not the track. The track's own width changes every time a run
    // is added, so watching it would re-enter this forever.
    if ('ResizeObserver' in window) new ResizeObserver(() => this.#layout()).observe(this);
  }

  /** The width of one run of the messages, margins included. */
  #runWidth() {
    let total = 0;

    for (const el of this.#originals) {
      const style = getComputedStyle(el);
      total +=
        el.getBoundingClientRect().width +
        (parseFloat(style.marginInlineStart) || 0) +
        (parseFloat(style.marginInlineEnd) || 0);
    }

    return total;
  }

  #layout() {
    const track = this.#track;
    if (!track) return;

    const runWidth = this.#runWidth();
    if (!runWidth) return;

    // Enough runs that a half covers the bar, so the window is never looking past the
    // end of the track. One extra beyond the strict minimum, because the mask fades the
    // outer 3rem and a half that ends exactly at the edge reveals the seam inside it.
    const runs = Math.ceil(this.clientWidth / runWidth) + 1;

    if (runs !== this.#runs) {
      this.#runs = runs;
      this.#rebuild(runs);
    }

    const distance = track.scrollWidth / 2;
    if (distance) {
      track.style.setProperty('--ks-ticker-duration', `${(distance / this.#pxPerSecond).toFixed(2)}s`);
    }
  }

  /** Lay down `runs` copies of the messages, twice over — one set per half. */
  #rebuild(runs) {
    const track = this.#track;
    if (!track) return;

    while (track.children.length > this.#originals.length) track.lastElementChild?.remove();

    for (let run = 1; run < runs * 2; run++) {
      for (const el of this.#originals) track.appendChild(this.#clone(el));
    }
  }

  /** @param {Element} el */
  #clone(el) {
    const copy = /** @type {Element} */ (el.cloneNode(true));

    copy.setAttribute('aria-hidden', 'true');
    copy.removeAttribute('id');

    // Theme-editor hooks must not be duplicated, or clicking the bar in the editor can
    // select a block that is not really there.
    for (const attr of Array.from(copy.attributes)) {
      if (attr.name.startsWith('data-shopify')) copy.removeAttribute(attr.name);
    }

    return copy;
  }
}

if (!customElements.get('announcement-ticker')) {
  customElements.define('announcement-ticker', AnnouncementTicker);
}
